import React from "react";
import {
  Card,
  CardContent,
  Typography,
  CardActions,
  CardActionArea,
  Button,
  Box,
  Tooltip,
} from "@mui/material";
import { FiberManualRecord, PrecisionManufacturing } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  formatLastUpdated,
  getBatteryColor,
  getBatteryIcon,
  getConnectionColor,
  getConnectionIcon,
  getConnectionTooltip,
  getRobotStatus,
  isDataStale,
} from "./deviceStatusUtils";
import { getDeviceImage } from "./deviceImages";
import useLanguage from "../hooks/useLanguage";
import { translateControl } from "../assets/variables/controlTranslations";

// Memoize the component to prevent unnecessary re-renders
const DeviceCard = React.memo(function DeviceCard({
  device,
  size,
  buttonText,
  urlPrefix = "/device/",
  disabled = true,
  // "control": minimal card for control users (picture, name, UUID, S/N, indicators)
  variant = "full",
}) {
  const navigate = useNavigate();
  const [language] = useLanguage();
  const t = (key) => translateControl(language, `deviceList.${key}`);

  const handleDetailsClick = () => {
    navigate(`${urlPrefix}${device.rc_device_id}`);
    console.log(`Navigating to: ${urlPrefix}${device.rc_device_id}`);
  };

  // Connection status
  const deviceOnline = device.connected === 1 || device.connected === true;

  // Get overall robot status
  const robotStatus = getRobotStatus(device);
  const isStale = robotStatus.isStale;

  const batteryLevel = device.battery_level || 0;
  const BatteryIcon = getBatteryIcon(batteryLevel, device);
  const batteryColor = getBatteryColor(batteryLevel, deviceOnline, isStale);

  // Connection status with stale data handling
  const ConnectionIcon = getConnectionIcon(deviceOnline, isStale);
  const connectionStatusColor = getConnectionColor(deviceOnline, isStale);
  const connectionStatusText = getConnectionTooltip(deviceOnline, isStale);

  const statusIndicators = (
    <Box
      sx={{
        position: "absolute",
        top: 12,
        right: 12,
        zIndex: 1,
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      {/* Battery Indicator */}
      <Tooltip
        title={
          isStale
            ? "Battery level unknown (stale data)"
            : `Battery: ${batteryLevel}%`
        }
        arrow
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <BatteryIcon
            sx={{
              fontSize: 18,
              color: batteryColor,
            }}
          />
          {!isStale && (
            <Typography
              variant="caption"
              sx={{
                fontSize: 10,
                fontWeight: "bold",
                color: batteryColor,
                lineHeight: 1,
              }}
            >
              {batteryLevel}%
            </Typography>
          )}
        </Box>
      </Tooltip>

      {/* Connection Status Indicator */}
      <Tooltip title={connectionStatusText} arrow>
        <ConnectionIcon
          sx={{
            fontSize: 16,
            color: connectionStatusColor,
          }}
        />
      </Tooltip>

      {/* Overall Robot Status Indicator */}
      <Tooltip
        title={
          robotStatus.isStale
            ? `No data since ${formatLastUpdated(robotStatus.lastUpdated)}`
            : robotStatus.status
        }
        arrow
      >
        <FiberManualRecord
          sx={{
            fontSize: 16,
            color: robotStatus.color,
            opacity: robotStatus.isStale ? 0.6 : 1,
          }}
        />
      </Tooltip>
    </Box>
  );

  if (variant === "control") {
    const robotImage = getDeviceImage(device);
    return (
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: 3,
          position: "relative",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CardActionArea
          // The whole card opens the device, not just the button (easier on a tablet)
          onClick={handleDetailsClick}
          disabled={disabled}
          aria-label={`Open ${device.name || device.rc_device_id}`}
          sx={{
            position: "relative",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "flex-start",
          }}
        >
          {statusIndicators}

          <Box
            sx={{
              height: 120,
              pt: 4.5,
              pb: 1,
              px: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "grey.50",
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            {robotImage ? (
              <Box
                component="img"
                src={robotImage}
                alt={
                  device.model_number || device.model || device.name || "Robot"
                }
                sx={{
                  maxHeight: "100%",
                  maxWidth: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              <PrecisionManufacturing
                sx={{ fontSize: 56, color: "grey.400" }}
              />
            )}
          </Box>

          <CardContent
            sx={{ flexGrow: 1, px: 1.5, py: 1, "&:last-child": { pb: 1 } }}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, lineHeight: 1.3 }}
              noWrap
            >
              {device.name || t("unnamed")}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
              noWrap
            >
              {t("uuid")}: {device.rc_device_id}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
              noWrap
            >
              {t("serial")}: {device.uk_device_id}
            </Typography>
          </CardContent>
        </CardActionArea>
        <CardActions sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
          <Button
            fullWidth
            size="large"
            sx={{ minHeight: 48 }}
            variant="outlined"
            disabled={disabled}
            onClick={handleDetailsClick}
          >
            {buttonText || t("controls")}
          </Button>
        </CardActions>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 3, boxShadow: 3, p: 2, position: "relative" }}>
      <CardActionArea
        // The whole card opens the device, not just the button
        onClick={handleDetailsClick}
        disabled={disabled}
        sx={{ position: "relative", borderRadius: 2 }}
      >
        {statusIndicators}

        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            {device.name || "Unnamed Device"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            UUID: {device.rc_device_id}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            S/N: {device.uk_device_id}
          </Typography>
          {size === "medium" ? (
            <>
              <Typography variant="body2" color="text.secondary">
                Status: {device.operating_state || "Unknown"}
              </Typography>
              {device.manufacturer === "Bosch" && (
                <Typography variant="body2" color="text.secondary">
                  Cleaning Step: {device.cleaning_step || "N/A"}
                </Typography>
              )}
              {device.manufacturer !== "Bosch" && (
                <Typography variant="body2" color="text.secondary">
                  State: {device.movement_state || "Unknown"}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                Type: {device.model || "N/A"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Customer: {device.customer_name || "Unassigned"}
              </Typography>
            </>
          ) : null}
        </CardContent>
      </CardActionArea>
      <CardActions>
        <Button
          size="small"
          variant="outlined"
          disabled={disabled}
          onClick={handleDetailsClick}
        >
          {buttonText || "Details"}
        </Button>
      </CardActions>
    </Card>
  );
});

export default DeviceCard;
